/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {hexTokenToNumber} from './CanRequestConversionUtils';

export interface CanRequestDraftRow {
  rowId: string;
  txId: string;
  canSource: number;
  txLen: number;
  rxId: string;
  rxMask: string;
  canSpeed: string;
  txPayload: string;
  label: string;
  errors?: Record<string, boolean>;
}

export const DATA_MAX_BYTES = 8;
export const DEFAULT_CAN_SPEED = 500000;
export const DEFAULT_DLC = 8;
export const DEFAULT_TX_ID = '7E0';
export const DEFAULT_CAN_SOURCE = 1;
export const DEFAULT_RX_ID = '7E0';
export const DEFAULT_RX_MASK = 'BB8';
export const DEFAULT_VIN_READ_RX_ID = '7E8';
export const DEFAULT_VIN_READ_RX_MASK = '7E8';
export const DEFAULT_TX_PAYLOAD = '02-09-02-00-00-00-00-00';
export const MAX_LABEL_LENGTH = 120;
export const DEFAULT_VIN_READ_PAYLOADS = [
  '02-09-02-00-00-00-00-00',
  '03-22-F8-02-00-00-00-00',
  '03-22-F1-90-00-00-00-00'
];

/**
 * Creates a unique identifier for a draft row, shared across the CAN
 * request create grid and the check-in condition editor.
 *
 * ```typescript
 * import {createRowId} from './CanRequestCreateRowUtils';
 *
 * createRowId();
 * ```
 *
 * @returns A unique row identifier.
 */
export const createRowId = (): string => (
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

/**
 * Normalizes a user-provided CAN payload into uppercase dash-delimited bytes.
 *
 * ```typescript
 * import {normalizeHexPayload} from './CanRequestCreateRowUtils';
 *
 * normalizeHexPayload('0a1b2c');
 * ```
 *
 * @param   value The raw payload value to normalize.
 * @param   maxBytes The maximum number of bytes to retain.
 * @returns The normalized payload in `AA-BB` format.
 */
export const normalizeHexPayload = (value = '', maxBytes = DATA_MAX_BYTES): string => {
  const sanitized = `${value}`.toUpperCase().replace(/[^0-9A-F]/g, '');
  const limited   = sanitized.slice(0, maxBytes * 2);
  const pairs     = limited.match(/.{1,2}/g) || [];
  return pairs.join('-');
};

/**
 * Formats a payload for fixed-width grid display by padding missing bytes.
 *
 * ```typescript
 * import {formatHexPayloadForDisplay} from './CanRequestCreateRowUtils';
 *
 * formatHexPayloadForDisplay('AA-BB', 4);
 * ```
 *
 * @param   value The payload value to format.
 * @param   maxBytes The target number of bytes to display.
 * @returns The padded payload string for UI display.
 */
export const formatHexPayloadForDisplay = (value = '', maxBytes = DATA_MAX_BYTES): string => {
  const normalized = normalizeHexPayload(value, maxBytes);
  const tokens     = normalized.split('-').filter(Boolean);
  const padded     = [...tokens];
  while (padded.length < maxBytes) {
    padded.push('00');
  }
  return padded.slice(0, maxBytes).join('-');
};

/**
 * Resizes a payload to match the requested TX length by truncating or padding.
 *
 * ```typescript
 * import {resizePayloadToTxLen} from './CanRequestCreateRowUtils';
 *
 * resizePayloadToTxLen('AA-BB-CC', 2);
 * ```
 *
 * @param   value The payload value to resize.
 * @param   txLen The requested TX length in bytes.
 * @returns The payload constrained to the provided TX length.
 */
export const resizePayloadToTxLen = (value = '', txLen = DATA_MAX_BYTES): string => {
  const clampedTxLen = Math.max(1, Math.min(Number(txLen || DATA_MAX_BYTES), DATA_MAX_BYTES));
  const tokens       = normalizeHexPayload(value, DATA_MAX_BYTES).split('-').filter(Boolean);
  const resized      = tokens.slice(0, clampedTxLen);
  while (resized.length < clampedTxLen) {
    resized.push('00');
  }
  return resized.join('-');
};

/**
 * Determines whether the given value is a positive integer.
 *
 * ```typescript
 * import {isPositiveInteger} from './CanRequestCreateRowUtils';
 *
 * isPositiveInteger('4');
 * ```
 *
 * @param   value The value to evaluate.
 * @returns `true` when the value is a positive integer, otherwise `false`.
 */
export const isPositiveInteger = (value: string | number): boolean => {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0;
};

/**
 * Checks whether the given value is a valid hexadecimal token.
 *
 * ```typescript
 * import {isValidHexValue} from './CanRequestCreateRowUtils';
 *
 * isValidHexValue('0x1A');
 * ```
 *
 * @param   value The value to validate.
 * @returns `true` when the value matches the hex format, otherwise `false`.
 */
export const isValidHexValue = (value: string): boolean => {
  const normalized = `${value || ''}`.trim();
  return /^(?:0x)?[0-9A-F]+$/i.test(normalized);
};

/**
 * Normalizes a hex token by trimming, removing `0x`, and uppercasing.
 *
 * ```typescript
 * import {normalizeHexValue} from './CanRequestCreateRowUtils';
 *
 * normalizeHexValue('0x1a');
 * ```
 *
 * @param   value The value to normalize.
 * @returns The normalized uppercase hex token.
 */
export const normalizeHexValue = (value: string): string => {
  const normalized = `${value || ''}`.trim().replace(/^0x/i, '').toUpperCase();
  return normalized;
};

/**
 * Normalizes a CAN request label by trimming and enforcing max length.
 *
 * @param   value The raw label value.
 * @returns The normalized label value.
 */
export const normalizeCanRequestLabel = (value: unknown = ''): string => {
  const str = String(value ?? '');
  return str.trim().slice(0, MAX_LABEL_LENGTH);
};

/**
 * Determines whether the provided value is valid positive hexadecimal.
 *
 * ```typescript
 * import {isPositiveHex} from './CanRequestCreateRowUtils';
 *
 * isPositiveHex('1A');
 * ```
 *
 * @param   value The value to evaluate.
 * @returns `true` when the value is valid positive hex, otherwise `false`.
 */
export const isPositiveHex = (value: string): boolean => {
  const isValidHex            = isValidHexValue(value);
  const numeric               = hexTokenToNumber(value);
  const isFinitePositiveNumber = Number.isFinite(numeric) && numeric > 0;
  return isValidHex && isFinitePositiveNumber;
};

/**
 * Validates that a payload matches TX length and byte formatting rules.
 *
 * ```typescript
 * import {validatePayload} from './CanRequestCreateRowUtils';
 *
 * validatePayload('AA-BB', 2);
 * ```
 *
 * @param   txPayload The payload value to validate.
 * @param   txLen The expected TX payload length.
 * @returns `true` when payload length and tokens are valid, else `false`.
 */
export const validatePayload = (txPayload: string, txLen: number): boolean => {
  const tokens            = `${txPayload}`.split('-').filter(Boolean);
  const hasExpectedLength = tokens.length === txLen;
  const hasValidTokens    = tokens.every((token) => /^[0-9A-F]{2}$/.test(token));
  return hasExpectedLength && hasValidTokens;
};

/**
 * Computes row-level validation flags for CAN request create grid rows.
 *
 * ```typescript
 * import {getRowErrors} from './CanRequestCreateRowUtils';
 *
 * getRowErrors(row);
 * ```
 *
 * @param   row The draft row to validate.
 * @param   maxBytes The maximum allowed TX bytes.
 * @returns A map of field names to validation error flags.
 */
export const getRowErrors = (row: CanRequestDraftRow, maxBytes = DATA_MAX_BYTES): Record<string, boolean> => {
  const normalizedRxId = normalizeHexValue(row.rxId);
  const normalizedRxMask = normalizeHexValue(row.rxMask);
  const normalizedTxId = normalizeHexValue(row.txId);

  return {
    txId      : !normalizedTxId || !isValidHexValue(normalizedTxId),
    canSource : !isPositiveInteger(row.canSource),
    txLen     : !Number.isInteger(Number(row.txLen)) || Number(row.txLen) < 1 || Number(row.txLen) > maxBytes,
    rxId      : !!normalizedRxId && !isValidHexValue(normalizedRxId),
    rxMask    : !!normalizedRxMask && !isValidHexValue(normalizedRxMask),
    canSpeed  : !isPositiveInteger(row.canSpeed),
    txPayload : !validatePayload(row.txPayload, Number(row.txLen))
  };
};

/**
 * Applies normalization and validation to a draft row in a single pass.
 *
 * ```typescript
 * import {withErrors} from './CanRequestCreateRowUtils';
 *
 * withErrors(row);
 * ```
 *
 * @param   row The draft row to normalize and validate.
 * @param   maxBytes The maximum allowed TX bytes.
 * @returns The normalized row with the computed `errors` map.
 */
export const withErrors = (row: CanRequestDraftRow, maxBytes = DATA_MAX_BYTES): CanRequestDraftRow => ({
  ...row,
  txId      : normalizeHexValue(row.txId),
  rxId      : normalizeHexValue(row.rxId),
  rxMask    : normalizeHexValue(row.rxMask),
  txPayload : normalizeHexPayload(row.txPayload, maxBytes),
  label     : normalizeCanRequestLabel(row.label),
  errors    : getRowErrors(row, maxBytes)
});

/**
 * Determines whether a row has any validation errors present.
 *
 * ```typescript
 * import {hasRowErrors} from './CanRequestCreateRowUtils';
 *
 * hasRowErrors(row);
 * ```
 *
 * @param   row The row containing error flags.
 * @returns `true` when at least one field is invalid, otherwise `false`.
 */
export const hasRowErrors = (row: CanRequestDraftRow): boolean => {
  return Object.values(row.errors || {}).some(Boolean);
};

/**
 * Creates a default draft row with normalized values and computed errors.
 *
 * ```typescript
 * import {createDefaultRow} from './CanRequestCreateRowUtils';
 *
 * createDefaultRow('row-1');
 * ```
 *
 * @param   rowId The unique row identifier.
 * @returns The initialized draft row.
 */
export const createDefaultRow = (rowId: string): CanRequestDraftRow => withErrors({
  rowId,
  txId      : DEFAULT_TX_ID,
  canSource : DEFAULT_CAN_SOURCE,
  txLen     : DEFAULT_DLC,
  rxId      : DEFAULT_RX_ID,
  rxMask    : DEFAULT_RX_MASK,
  canSpeed  : `${DEFAULT_CAN_SPEED}`,
  txPayload : DEFAULT_TX_PAYLOAD,
  label     : ''
});

/**
 * Creates the default VIN-read draft rows with normalized values and errors.
 *
 * ```typescript
 * import {createDefaultVinReadRows} from './CanRequestCreateRowUtils';
 *
 * createDefaultVinReadRows(['row-1', 'row-2', 'row-3']);
 * ```
 *
 * @param   rowIds The ordered row ids to assign to defaults.
 * @returns The initialized VIN-read draft rows.
 */
export const createDefaultVinReadRows = (rowIds: string[]): CanRequestDraftRow[] => {
  const rows: CanRequestDraftRow[] = [];
  DEFAULT_VIN_READ_PAYLOADS.forEach((payload, index) => {
    const rowId = rowIds[index];
    if (rowId) {
      rows.push(withErrors({
        rowId,
        txId      : DEFAULT_TX_ID,
        canSource : DEFAULT_CAN_SOURCE,
        txLen     : DEFAULT_DLC,
        rxId      : DEFAULT_VIN_READ_RX_ID,
        rxMask    : DEFAULT_VIN_READ_RX_MASK,
        canSpeed  : `${DEFAULT_CAN_SPEED}`,
        txPayload : payload,
        label     : ''
      }));
    }
  });
  return rows;
};

/**
 * Creates a cloned draft row with a new row id and recomputed validation.
 *
 * ```typescript
 * import {cloneDraftRow} from './CanRequestCreateRowUtils';
 *
 * cloneDraftRow(row, 'row-2');
 * ```
 *
 * @param   row The row to clone.
 * @param   rowId The new unique row identifier.
 * @returns The cloned row with normalized values and errors.
 */
export const cloneDraftRow = (row: CanRequestDraftRow, rowId: string): CanRequestDraftRow => withErrors({
  ...row,
  rowId
});
